import { AGENTIC_LOGIC_RULE_ENTRY_FILENAME } from "@intelligems/ig-types";
import type { MonacoTypeScriptCompanionModel } from "@src/components/CodeEditors/MonacoTypeScriptEditor";

/**
 * Editor-only declarations for the modules a rule's test suites import, so a
 * suite opened in the Advanced editor type-checks the way it runs.
 *
 * Both are hidden companions registered only while a test file is open (see
 * `WorkspaceMonacoConfig.testSuiteCompanions`): rule source must never
 * resolve `vitest` or the harness, and the bundler refuses both.
 *
 * The suite runner does not type-check, and neither declaration here tries to
 * be the full library. The Vitest surface is the one suites actually use, with
 * an index signature behind each API so a matcher or utility missing here reads
 * as `any` instead of a red squiggle on a test that runs fine.
 */

/**
 * An ambient `declare module`, so it needs no resolvable path — `node_modules`
 * placement mirrors the logger lib and keeps it out of the user's file tree.
 */
export const AGENTIC_LOGIC_VITEST_LIB_URI =
  "file:///node_modules/@intelligems/agentic-logic-vitest/index.d.ts";

export const AGENTIC_LOGIC_VITEST_AMBIENT_DTS = `declare module "vitest" {
  type Awaitable<T> = T | PromiseLike<T>;
  type TestFunction = () => Awaitable<unknown>;
  type EachArgs<T> = T extends readonly unknown[] ? T : [T];

  interface TestAPI {
    (name: string, fn?: TestFunction, timeout?: number): void;
    (name: string, options: Record<string, unknown>, fn?: TestFunction): void;
    only: TestAPI;
    skip: TestAPI;
    concurrent: TestAPI;
    sequential: TestAPI;
    fails: TestAPI;
    todo: (name: string) => void;
    skipIf: (condition: unknown) => TestAPI;
    runIf: (condition: unknown) => TestAPI;
    each: <T>(
      cases: ReadonlyArray<T>
    ) => (
      name: string,
      fn: (...args: EachArgs<T>) => Awaitable<unknown>,
      timeout?: number
    ) => void;
    [modifier: string]: any;
  }

  interface SuiteAPI {
    (name: string, fn?: () => void): void;
    (name: string, options: Record<string, unknown>, fn?: () => void): void;
    only: SuiteAPI;
    skip: SuiteAPI;
    concurrent: SuiteAPI;
    sequential: SuiteAPI;
    todo: (name: string) => void;
    skipIf: (condition: unknown) => SuiteAPI;
    runIf: (condition: unknown) => SuiteAPI;
    each: <T>(
      cases: ReadonlyArray<T>
    ) => (name: string, fn: (...args: EachArgs<T>) => void) => void;
    [modifier: string]: any;
  }

  interface Assertion<T = any> {
    not: Assertion<T>;
    resolves: Assertion<Awaited<T>>;
    rejects: Assertion<unknown>;
    toBe(expected: unknown): void;
    toEqual(expected: unknown): void;
    toStrictEqual(expected: unknown): void;
    toMatchObject(expected: object | object[]): void;
    toContain(item: unknown): void;
    toContainEqual(item: unknown): void;
    toHaveLength(length: number): void;
    toHaveProperty(path: string | readonly (string | number)[], value?: unknown): void;
    toBeDefined(): void;
    toBeUndefined(): void;
    toBeNull(): void;
    toBeTruthy(): void;
    toBeFalsy(): void;
    toBeNaN(): void;
    toBeGreaterThan(expected: number | bigint): void;
    toBeGreaterThanOrEqual(expected: number | bigint): void;
    toBeLessThan(expected: number | bigint): void;
    toBeLessThanOrEqual(expected: number | bigint): void;
    toBeCloseTo(expected: number, numDigits?: number): void;
    toBeInstanceOf(expected: unknown): void;
    toBeTypeOf(expected: string): void;
    toBeOneOf(expected: readonly unknown[]): void;
    toMatch(expected: string | RegExp): void;
    toThrow(expected?: unknown): void;
    toThrowError(expected?: unknown): void;
    toSatisfy(predicate: (value: T) => boolean, message?: string): void;
    toHaveBeenCalled(): void;
    toHaveBeenCalledTimes(times: number): void;
    toHaveBeenCalledWith(...args: unknown[]): void;
    toHaveBeenLastCalledWith(...args: unknown[]): void;
    toHaveBeenNthCalledWith(nth: number, ...args: unknown[]): void;
    toHaveReturned(): void;
    toHaveReturnedWith(value: unknown): void;
    toMatchSnapshot(message?: string): void;
    toMatchInlineSnapshot(snapshot?: string): void;
    [matcher: string]: any;
  }

  interface AsymmetricMatchers {
    anything(): any;
    any(constructor: unknown): any;
    objectContaining<T = any>(expected: T): any;
    arrayContaining<T = unknown>(expected: readonly T[]): any;
    stringContaining(expected: string): any;
    stringMatching(expected: string | RegExp): any;
    closeTo(expected: number, numDigits?: number): any;
  }

  interface ExpectStatic extends AsymmetricMatchers {
    <T>(actual: T, message?: string): Assertion<T>;
    soft: <T>(actual: T, message?: string) => Assertion<T>;
    not: AsymmetricMatchers;
    assertions(count: number): void;
    hasAssertions(): void;
    unreachable(message?: string): never;
    extend(matchers: Record<string, unknown>): void;
    [utility: string]: any;
  }

  type Procedure = (...args: any[]) => any;

  interface MockResult {
    type: "return" | "throw" | "incomplete";
    value: unknown;
  }

  interface Mock<T extends Procedure = Procedure> {
    (...args: Parameters<T>): ReturnType<T>;
    mock: {
      calls: Parameters<T>[];
      results: MockResult[];
      lastCall: Parameters<T> | undefined;
    };
    mockReturnValue(value: ReturnType<T>): this;
    mockReturnValueOnce(value: ReturnType<T>): this;
    mockResolvedValue(value: Awaited<ReturnType<T>>): this;
    mockResolvedValueOnce(value: Awaited<ReturnType<T>>): this;
    mockRejectedValue(error: unknown): this;
    mockRejectedValueOnce(error: unknown): this;
    mockImplementation(fn: T): this;
    mockImplementationOnce(fn: T): this;
    mockName(name: string): this;
    getMockName(): string;
    mockClear(): this;
    mockReset(): this;
    mockRestore(): void;
    [member: string]: any;
  }

  interface VitestUtils {
    fn<T extends Procedure = Procedure>(implementation?: T): Mock<T>;
    spyOn(object: object, method: string, accessType?: "get" | "set"): Mock;
    isMockFunction(fn: unknown): boolean;
    clearAllMocks(): VitestUtils;
    resetAllMocks(): VitestUtils;
    restoreAllMocks(): VitestUtils;
    useFakeTimers(config?: Record<string, unknown>): VitestUtils;
    useRealTimers(): VitestUtils;
    advanceTimersByTime(ms: number): VitestUtils;
    advanceTimersByTimeAsync(ms: number): Promise<VitestUtils>;
    runAllTimers(): VitestUtils;
    runAllTimersAsync(): Promise<VitestUtils>;
    runOnlyPendingTimers(): VitestUtils;
    setSystemTime(time: number | string | Date): VitestUtils;
    waitFor<T>(
      callback: () => T | Promise<T>,
      options?: number | { timeout?: number; interval?: number }
    ): Promise<T>;
    [utility: string]: any;
  }

  type HookFunction = () => Awaitable<unknown>;

  export const test: TestAPI;
  export const it: TestAPI;
  export const describe: SuiteAPI;
  export const suite: SuiteAPI;
  export const expect: ExpectStatic;
  export const vi: VitestUtils;
  export const vitest: VitestUtils;
  export const assert: any;
  export function beforeEach(fn: HookFunction, timeout?: number): void;
  export function afterEach(fn: HookFunction, timeout?: number): void;
  export function beforeAll(fn: HookFunction, timeout?: number): void;
  export function afterAll(fn: HookFunction, timeout?: number): void;
  export type { Assertion, ExpectStatic, Mock, VitestUtils };
}
`;

/**
 * A ROOT sibling of the entry file, so a suite's relative
 * `import … from "./test-harness"` resolves to it exactly as the runner's
 * injected `test-harness.ts` does.
 */
export const AGENTIC_LOGIC_TEST_HARNESS_LIB_URI = "file:///test-harness.d.ts";

/**
 * Mirrors `installAgenticLogicTestSdkRuntime` and its option/handle types in
 * node-backend's `agentic-logic-published-rule-runtime.ts`, the module the
 * runner injects as `./test-harness`.
 */
export const AGENTIC_LOGIC_TEST_HARNESS_DTS = `/** One fake-sdk read the rule performed: the method and the arguments it passed, by reference. */
export interface AgenticLogicTestSdkCall {
  method: string;
  args: unknown[];
}

export interface AgenticLogicTestSdkHandle {
  /**
   * Every fake-sdk read since this install, in call order. Live: reads made
   * after you capture the handle appear in it. \`seededShuffle\` is never
   * recorded. Assert reads through this rather than a recorder of your own.
   */
  readonly calls: readonly AgenticLogicTestSdkCall[];
  /** Removes this runtime. Installing again also replaces it. */
  uninstall(): void;
}

export interface AgenticLogicTestSdkRuntimeOptions {
  /**
   * The fake data sdk: plain functions returning fixture data for exactly the
   * reads the rule performs. \`seededShuffle\` and the chain combinators stay
   * the real implementations.
   */
  sdk: Record<string, unknown>;
  /** Seed inputs for the real \`seededShuffle\`. */
  visitorId?: string;
  ruleVersion?: string;
  /** How long each faked read takes, keyed by sdk method name, in ms. */
  readDurationsMs?: Record<string, number>;
}

/**
 * Installs the ambient \`sdk\`, \`attempt\`, \`firstNonEmpty\` and \`fillTo\` over a
 * fake data sdk, so the suite drives the real \`evaluate\`. Call it in
 * \`beforeEach\`; each install starts a fresh call record.
 */
export declare function installAgenticLogicTestSdkRuntime(
  options: AgenticLogicTestSdkRuntimeOptions
): AgenticLogicTestSdkHandle;
`;

export const AGENTIC_LOGIC_TEST_SUITE_COMPANIONS: readonly MonacoTypeScriptCompanionModel[] =
  [
    {
      uri: AGENTIC_LOGIC_VITEST_LIB_URI,
      content: AGENTIC_LOGIC_VITEST_AMBIENT_DTS,
    },
    {
      uri: AGENTIC_LOGIC_TEST_HARNESS_LIB_URI,
      content: AGENTIC_LOGIC_TEST_HARNESS_DTS,
    },
  ];

const ENTRY_MODULE = `./${AGENTIC_LOGIC_RULE_ENTRY_FILENAME.replace(/\.ts$/, "")}`;

/**
 * What "Add test" seeds into an empty user suite: the imports every suite
 * needs and one test that passes against any rule honouring the zero-slot
 * contract, so the first run of an unedited skeleton is green.
 */
export const AGENTIC_LOGIC_USER_SUITE_SKELETON = `import { beforeEach, describe, expect, test } from "vitest";

import { evaluate } from "${ENTRY_MODULE}";
import {
  type AgenticLogicTestSdkHandle,
  installAgenticLogicTestSdkRuntime,
} from "./test-harness";
import type { AgenticLogicInvocation } from "./types";

let harness: AgenticLogicTestSdkHandle;

beforeEach(() => {
  // Fake only the sdk reads your rule makes, returning fixture data, e.g.
  // getProducts: async () => ({ items: [], pageInfo: { hasNextPage: false, endCursor: null } })
  harness = installAgenticLogicTestSdkRuntime({ sdk: {} });
});

describe("my tests", () => {
  test("answers an empty envelope when the placement needs no slots", async () => {
    // Fill in the request fields your rule reads.
    const invocation = {
      request: { evaluatedAt: "2026-01-01T12:00:00.000Z" },
      context: { slotsNeeded: 0 },
    } as unknown as AgenticLogicInvocation;

    const envelope = await evaluate(invocation);

    expect(envelope.items).toEqual([]);
    expect(harness.calls).toEqual([]);
  });
});
`;
